import React, { useState } from 'react';

const GRAPH_BUILDER_API = process.env.REACT_APP_GRAPH_BUILDER_API_URL || 'http://localhost:8000';

const STEP_LABELS = [
  { key: 'idle', label: 'Ready', icon: '🧠', color: '#64748b' },
  { key: 'resolve', label: 'Resolve — Activate SNN', icon: '⚡', color: '#3b82f6' },
  { key: 'eligibility', label: 'Eligibility — Synapse Scores', icon: '🔬', color: '#8b5cf6' },
  { key: 'feedback', label: 'Feedback — Apply Reward', icon: '📊', color: '#f59e0b' },
  { key: 'deltas', label: 'Weight Deltas — Learning', icon: '🧬', color: '#10b981' },
];

const DemoHebbianPanel = ({ graphId, workspaceId }) => {
  const [step, setStep] = useState(0); // 0=idle, 1=resolve, 2=eligibility, 3=feedback, 4=deltas
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [outcome, setOutcome] = useState('readmitted');
  const [error, setError] = useState(null);

  const handleRun = async () => {
    setError(null);
    setResult(null);
    setRunning(true);

    // Step 1: Resolve
    setStep(1);
    await new Promise(r => setTimeout(r, 800));

    try {
      const res = await fetch(`${GRAPH_BUILDER_API}/api/cortex/demo/hebbian`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          graph_id: graphId || '3d688f7c-c6a4-4894-ae53-ed9f5668b21c',
          workspace_id: workspaceId || '3120785d-027c-4eda-b20c-adf7cc31de0d',
          outcome,
          shadow: true,
        }),
      });
      const data = await res.json();

      if (!data.success && data.detail) {
        throw new Error(data.detail);
      }

      // Step 2: Eligibility
      setStep(2);
      setResult(data);
      await new Promise(r => setTimeout(r, 1200));

      // Step 3: Feedback
      setStep(3);
      await new Promise(r => setTimeout(r, 1200));

      // Step 4: Deltas
      setStep(4);
    } catch (err) {
      setError(err.message);
      setStep(0);
    } finally {
      setRunning(false);
    }
  };

  const handleReset = () => {
    setStep(0);
    setResult(null);
    setError(null);
  };

  const currentStep = STEP_LABELS[step];

  return (
    <div style={{
      background: '#0f172a', borderRadius: '8px', border: '1px solid #1e293b',
      padding: '16px', height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#f1f5f9' }}>
            Hebbian Plasticity — Live
          </div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
            Three-Factor Rule: dW = lr × reward × eligibility
          </div>
        </div>
        {step === 0 ? (
          <button
            onClick={handleRun}
            disabled={running}
            style={{
              padding: '6px 16px', borderRadius: '6px', border: 'none',
              background: '#8b5cf6', color: '#fff', fontWeight: 600, fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            ▶ Run Hebbian Demo
          </button>
        ) : (
          <button onClick={handleReset} style={{
            padding: '6px 16px', borderRadius: '6px', border: '1px solid #334155',
            background: 'transparent', color: '#94a3b8', fontWeight: 600, fontSize: '13px',
            cursor: 'pointer',
          }}>
            ↺ Reset
          </button>
        )}
      </div>

      {/* Outcome selector */}
      {step === 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>Simulated outcome:</span>
          {['readmitted', 'not_readmitted', 'neutral'].map(o => (
            <button
              key={o}
              onClick={() => setOutcome(o)}
              style={{
                padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                border: outcome === o ? '1px solid #8b5cf6' : '1px solid #334155',
                background: outcome === o ? '#2e1065' : 'transparent',
                color: outcome === o ? '#c4b5fd' : '#64748b',
                cursor: 'pointer',
              }}
            >
              {o.replace('_', ' ')}
            </button>
          ))}
        </div>
      )}

      {/* Step progress */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '14px' }}>
        {STEP_LABELS.slice(1).map((s, i) => (
          <div key={s.key} style={{
            flex: 1, height: '4px', borderRadius: '2px',
            background: i + 1 <= step ? s.color : '#1e293b',
            transition: 'background 0.5s ease',
          }} />
        ))}
      </div>

      {/* Current step indicator */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px',
        padding: '8px 12px', background: '#1e293b', borderRadius: '6px',
      }}>
        <span style={{ fontSize: '18px' }}>{currentStep.icon}</span>
        <span style={{ color: currentStep.color, fontWeight: 600, fontSize: '13px' }}>
          {currentStep.label}
        </span>
        {running && <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '11px' }}>Processing…</span>}
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {error && (
          <div style={{
            padding: '10px', background: '#450a0a', border: '1px solid #7f1d1d',
            borderRadius: '6px', color: '#fca5a5', fontSize: '12px', marginBottom: '8px',
          }}>
            {error}
          </div>
        )}

        {/* Step 2: Eligibility table */}
        {step >= 2 && result?.resolve_phase?.eligible_synapses && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#c4b5fd', marginBottom: '6px' }}>
              Eligible Synapses ({result.resolve_phase.total_eligible})
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>
              Query: "{result.resolve_phase.query}"
            </div>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#64748b', textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px', borderBottom: '1px solid #1e293b' }}>Synapse</th>
                  <th style={{ padding: '4px 8px', borderBottom: '1px solid #1e293b' }}>Weight</th>
                  <th style={{ padding: '4px 8px', borderBottom: '1px solid #1e293b' }}>Eligibility</th>
                </tr>
              </thead>
              <tbody>
                {result.resolve_phase.eligible_synapses.map((s, i) => (
                  <tr key={i} style={{ color: '#cbd5e1' }}>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #0f172a' }}>
                      <span style={{ color: '#93c5fd' }}>{s.source_label}</span>
                      <span style={{ color: '#475569' }}> → </span>
                      <span style={{ color: '#a5b4fc' }}>{s.target_label}</span>
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #0f172a', fontFamily: 'monospace' }}>
                      {s.weight}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #0f172a' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '50px', height: '4px', background: '#1e293b', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${s.eligibility * 100}%`, background: '#8b5cf6', borderRadius: '2px' }} />
                        </div>
                        <span style={{ fontFamily: 'monospace' }}>{s.eligibility}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Step 3: Feedback */}
        {step >= 3 && result?.feedback_phase && (
          <div style={{
            marginBottom: '12px', padding: '10px', background: '#1c1917',
            borderRadius: '6px', border: '1px solid #44403c',
          }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#fbbf24', marginBottom: '6px' }}>
              Feedback Applied
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
              <span>Outcome: <strong style={{ color: '#fbbf24' }}>{result.feedback_phase.outcome}</strong></span>
              <span>Reward: <strong style={{ color: '#fbbf24' }}>{result.feedback_phase.reward}</strong></span>
              <span>Learning rate: <strong style={{ color: '#fbbf24' }}>{result.feedback_phase.learning_rate}</strong></span>
              <span>Shadow: <strong style={{ color: result.feedback_phase.shadow ? '#22c55e' : '#ef4444' }}>{result.feedback_phase.shadow ? 'ON' : 'OFF'}</strong></span>
            </div>
          </div>
        )}

        {/* Step 4: Weight deltas */}
        {step >= 4 && result?.feedback_phase?.weight_updates && (
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#6ee7b7', marginBottom: '6px' }}>
              Weight Deltas (Three-Factor Rule)
            </div>
            {result.feedback_phase.weight_updates.map((u, i) => (
              <div key={i} style={{
                padding: '8px 10px', marginBottom: '6px', background: '#0f2918',
                borderRadius: '6px', border: '1px solid #166534', fontSize: '11px',
              }}>
                <div style={{ color: '#cbd5e1', marginBottom: '4px' }}>
                  <strong>{u.source_label}</strong>
                  <span style={{ color: '#475569' }}> → </span>
                  <strong>{u.target_label}</strong>
                </div>
                <div style={{ display: 'flex', gap: '16px', color: '#94a3b8' }}>
                  <span>Before: <strong style={{ color: '#94a3b8' }}>{u.weight_before}</strong></span>
                  <span style={{ color: u.dW > 0 ? '#4ade80' : u.dW < 0 ? '#f87171' : '#94a3b8', fontWeight: 700 }}>
                    dW = {u.dW > 0 ? '+' : ''}{u.dW}
                  </span>
                  <span>After: <strong style={{ color: '#4ade80' }}>{u.weight_after}</strong></span>
                </div>
                <div style={{ fontSize: '10px', color: '#475569', marginTop: '2px', fontFamily: 'monospace' }}>
                  {u.formula}
                </div>
              </div>
            ))}

            {/* Summary */}
            {result.summary && (
              <div style={{
                marginTop: '10px', padding: '10px', background: '#052e16',
                border: '1px solid #166534', borderRadius: '6px', fontSize: '12px', color: '#86efac',
              }}>
                ✅ {result.summary.synapses_updated} synapses updated • Total ΔW = {result.summary.total_dW} • Max |ΔW| = {result.summary.max_dW}
                <div style={{ fontSize: '10px', color: '#4ade80', marginTop: '4px' }}>
                  Safety: {result.summary.safety}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 0 && !error && (
          <div style={{ color: '#475569', textAlign: 'center', padding: '40px 0', fontSize: '13px' }}>
            Run the Hebbian demo to see the three-factor<br />
            plasticity rule applied to readmission risk synapses.
            <div style={{ fontSize: '11px', marginTop: '8px', color: '#334155' }}>
              dW = learning_rate × reward × eligibility
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoHebbianPanel;
